import { FC } from "react";
import { ICellRendererParams } from "ag-grid-community";
import Typography from "@mui/material/Typography";
import { Box, List, ListItem, Stack, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { getPlannedBudgetsWithLabels } from "../../utils/helpers/getPlannedBudgetsWithLabels";
import { isCurrentPlannedBudgetPeriod } from "../../utils/helpers/isCurrentPlannedBudgetPeriod";
import { Budget } from "@/services/cloudchipr.api";
import { money } from "@/utils/numeral/money";
import { useAppSelector } from "@/store/hooks";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { TypographyWithCopy } from "@/components/common/TypographyWithCopy";

export const BudgetTypeCell: FC<ICellRendererParams<Budget>> = ({
  data: budget,
}) => {
  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );

  if (!budget) {
    return;
  }

  const type = budget?.type;
  const period = budget?.period;
  const plannedAmounts = budget?.planned_amounts;
  const startDate = budget?.start_date;

  if (type === "fixed") {
    return <TypographyWithCopy value="Fixed" variant="body2" />;
  }

  const plannedBudgets = getPlannedBudgetsWithLabels({
    period,
    plannedAmounts: plannedAmounts ?? undefined,
    startDate,
    quarterStartMonth,
  });

  return (
    <Tooltip
      title={
        <List>
          {plannedBudgets.map((plannedBudget) => {
            const isSelected = isCurrentPlannedBudgetPeriod(
              plannedBudget.label,
              period,
              quarterStartMonth,
            );

            return (
              <ListItem
                key={plannedBudget.label}
                sx={{
                  bgcolor: isSelected ? "grey.200" : "inherit",
                }}
              >
                <Stack direction="row" spacing={4} key={plannedBudget.label}>
                  <Typography variant="caption">
                    {plannedBudget.label}
                  </Typography>
                  <Typography variant="caption">
                    {money(plannedBudget.amount)}
                  </Typography>
                </Stack>
              </ListItem>
            );
          })}
        </List>
      }
      placement="bottom"
      componentsProps={{
        tooltip: { sx: customTooltipStyles },
      }}
    >
      <Box>
        <TypographyWithCopy
          value="Planned"
          component="span"
          variant="body2"
          color="text.primary"
          sx={underlineStyles}
        />
      </Box>
    </Tooltip>
  );
};

const customTooltipStyles = {
  color: "text.primary",
  bgcolor: "background.paper",
  "& .MuiTooltip-arrow": {
    color: "background.paper",
  },
  boxShadow: 7,
  p: 0,
};

const underlineStyles = {
  textDecoration: "underline",
  textDecorationStyle: "dashed",
  textUnderlineOffset: 4,
  textDecorationColor: grey["A400"],
};
